import { useEffect, useRef } from "react";
import { BREAKPOINT } from "@/configs/breakpoints";
import { calculateCardHeight, getEffectiveChipsHeight } from "@/features/cards/shared/card-shell/card-geometry";
import { CARD_SLOT } from "@/features/cards/shared/card-routing/card-config";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useStore } from "@/store/store";
import type MapEntityBase from "../../map-entities/map-entity-base";
import { MIN_HEIGHT_DIFFERENCE_FOR_REFINEMENT_PX, REFINEMENT_DELAY_MS } from "../../panning/pan-constants";
import { panTo } from "../../panning/pan-to";

const panToAvoidCard = (focusedMapEntity: MapEntityBase, isRepositioning?: boolean): void => {
    panTo(focusedMapEntity, {
        shouldPanRegardlessOfObscuration: isRepositioning ?? false,
    });
};

export const shouldPan = (
    isMobileDimensions: boolean,
    isCardOpen: boolean,
    selectedMapEntity: MapEntityBase | null,
): boolean => {
    // AOI selection should not auto-pan/zoom on mobile
    const category = selectedMapEntity?.model?.category;
    if (category === "aoi") {
        return false;
    }
    return Boolean(isMobileDimensions && isCardOpen);
};

/**
 * Hook that pans the map to ensure the selected device marker is visible when a card opens.
 * Uses predicted card height for immediate panning, then refines with actual measured height.
 * The 2-step timing is preserved to handle cards whose height changes after initial render.
 */
export const usePanToAvoidCard = () => {
    const isMobileDimensions = useMediaQuery(BREAKPOINT.DEVICE_CARD_PAN);
    const isCardOpen = useStore((state) => state.slots[CARD_SLOT.BOTTOM_RIGHT].isOpen);
    const selectedMapEntity = useStore((state) => state.selectedMapEntity);
    const cardHeight = useStore((state) => state.slots[CARD_SLOT.BOTTOM_RIGHT].height);
    const cardType = useStore((state) => state.slots[CARD_SLOT.BOTTOM_RIGHT].cardType);
    const chipsHeight = useStore((state) => state.chipsHeight);
    const isRepositioning = useStore((state) => state.isRepositioningDevice);

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const rafRef = useRef<number | null>(null);
    const hasInitialPannedRef = useRef(false);

    const cleanupPendingOperations = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    };

    // Pan immediately when card opens, then refine once the actual height is measured
    useEffect(() => {
        if (!selectedMapEntity || !shouldPan(isMobileDimensions, isCardOpen, selectedMapEntity)) {
            // Clear the initial-pan marker when a card is not open or when panning is bypassed, so a later eligible
            // selection (e.g. zone -> device while card stays open) still gets an initial pan.
            hasInitialPannedRef.current = false;
            return () => {
                cleanupPendingOperations();
            };
        }

        // Immediate pan after a single frame (card dimensions may not be set in store yet)
        if (!hasInitialPannedRef.current) {
            rafRef.current = requestAnimationFrame(() => {
                const effectiveChipsHeight = getEffectiveChipsHeight(chipsHeight, cardType);
                const expectedHeight = calculateCardHeight(isMobileDimensions, effectiveChipsHeight);
                // Always mark attempted so refinement can run once actual height is measured,
                // even if height is 0 on this frame (e.g. card not yet painted)
                hasInitialPannedRef.current = true;
                if (expectedHeight > 0) {
                    panToAvoidCard(selectedMapEntity, isRepositioning);
                }
                rafRef.current = null;
            });
        }

        // Refinement: re-pan if actual height differs meaningfully from predicted
        if (cardHeight > 0 && hasInitialPannedRef.current) {
            timeoutRef.current = setTimeout(() => {
                const effectiveChipsHeight = getEffectiveChipsHeight(chipsHeight, cardType);
                const expectedHeight = calculateCardHeight(isMobileDimensions, effectiveChipsHeight);
                const heightDifference = Math.abs(cardHeight - expectedHeight);

                if (heightDifference > MIN_HEIGHT_DIFFERENCE_FOR_REFINEMENT_PX) {
                    rafRef.current = requestAnimationFrame(() => {
                        panToAvoidCard(selectedMapEntity, isRepositioning);
                        rafRef.current = null;
                    });
                }
            }, REFINEMENT_DELAY_MS);
        }

        return () => {
            cleanupPendingOperations();
        };
    }, [isCardOpen, selectedMapEntity, isMobileDimensions, cardHeight, cardType, chipsHeight, isRepositioning]);
};
